import SiteLayout from "../components/SiteLayout";
import SEO from "../components/SEO";
import CTABanner from "../components/CTABanner";
import { Brain, Pill, ClipboardList, Users, Video, Check } from "lucide-react";

const services = [
 {
 icon: Brain,
 title: "Psychiatric Evaluation & Diagnosis",
 desc: "A 50-60 minute conversation that explores symptoms, history, and goals. We diagnose carefully and explain everything in plain language so you understand exactly what's happening, and why.",
 outcomes: ["Clear, written diagnostic impression", "A starting plan you helped shape", "Time and space to ask anything"],
 },
 {
 icon: Pill,
 title: "Medication Management",
 desc: "Thoughtful prescribing, careful adjustments, and honest conversations about benefits, trade-offs, and alternatives. Medication is one tool among many, we help you use it well.",
 outcomes: ["Evidence-based prescribing", "Symptom and side-effect monitoring", "Coordination with your pharmacy"],
 },
 {
 icon: ClipboardList,
 title: "Individualised Treatment Planning",
 desc: "Every plan is shaped to fit your life, identity, and goals. We integrate medication, therapy referrals, lifestyle support, and family involvement when you want it.",
 outcomes: ["Tailored to your circumstances", "Adjusted as you grow", "Centred on your priorities"],
 },
 {
 icon: Users,
 title: "Collaborative Care Approach",
 desc: "We work hand-in-hand with primary care, therapists, schools, and family when invited. The goal: a unified plan that doesn't make you the messenger.",
 outcomes: ["Shared notes (with consent)", "Joint planning calls when helpful", "You stay at the centre"],
 },
 {
 icon: Video,
 title: "Telehealth Services",
 desc: "Secure video visits from your phone, laptop, or tablet, no commute, no waiting room, no scheduling acrobatics. Same clinical depth, delivered where you are.",
 outcomes: ["HIPAA-aligned video", "Evening & weekend slots", "Care from any private space"],
 },
];

export default function Services() {
 return (
 <SiteLayout>
 <SEO
 title="Psychiatric Services in Maryland"
 description="Full-scope online psychiatry: comprehensive evaluations, medication management, treatment planning, and telehealth follow-ups for adults across Maryland."
 path="/services"
 />
 <section data-testid="services-hero" className="container-pad max-w-7xl mx-auto pt-14 lg:pt-20 pb-14">
 <div className="grid lg:grid-cols-12 gap-12 items-center">
 <div className="lg:col-span-7">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Services</p>
 <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink text-balance">
 A full spectrum of psychiatric care, <span className="italic text-sage-600">delivered with warmth.</span>
 </h1>
 <p className="mt-6 text-lg text-muted_text leading-relaxed max-w-2xl">
 Whether you're starting care, switching providers, or returning after a break, every service is built to meet you exactly where you are.
 </p>
 </div>
 <div className="lg:col-span-5">
 <div className="relative">
 <img
 src="/services-hero.png"
 alt="Our Services, compassionate care, expert support. A healthier mind, a better life."
 className="rounded-3xl w-full h-auto object-contain shadow-[0_30px_60px_-30px_rgba(26,43,51,0.3)] bg-cream"
 loading="lazy"
 />
 <div className="absolute -top-6 -left-6 w-32 h-32 rounded-3xl bg-gold-500/15 -z-10" />
 <div className="absolute -bottom-8 -right-6 w-40 h-40 rounded-3xl bg-sage-500/15 -z-10" />
 </div>
 </div>
 </div>
 </section>

 <section data-testid="services-list" className="container-pad max-w-7xl mx-auto pb-12">
 <div className="grid gap-6">
 {services.map((s, i) => (
 <article key={s.title} data-testid={`service-${i}`} className="card-soft p-8 lg:p-10 grid lg:grid-cols-12 gap-8 items-start hover:-translate-y-0.5 transition-transform duration-300">
 <div className="lg:col-span-1">
 <div className="w-14 h-14 rounded-2xl bg-sage-50 flex items-center justify-center text-sage-700">
 <s.icon className="w-7 h-7" strokeWidth={1.5} />
 </div>
 </div>
 <div className="lg:col-span-7">
 <span className="font-heading text-gold-600 text-xs tracking-[0.22em]">SERVICE 0{i + 1}</span>
 <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-semibold text-ink">{s.title}</h2>
 <p className="mt-4 text-muted_text leading-relaxed">{s.desc}</p>
 </div>
 <div className="lg:col-span-4">
 <p className="text-xs uppercase tracking-[0.2em] font-semibold text-sage-700">What you can expect</p>
 <ul className="mt-4 space-y-3">
 {s.outcomes.map((o) => (
 <li key={o} className="inline-flex items-start gap-2 text-sm text-ink">
 <Check className="w-4 h-4 mt-0.5 text-sage-600 shrink-0" strokeWidth={2} /> {o}
 </li>
 ))}
 </ul>
 </div>
 </article>
 ))}
 </div>
 </section>

 <CTABanner headline="Find the service that fits your moment." sub="If you're unsure where to begin, book a consultation, we'll help you map it out together." />
 </SiteLayout>
 );
}
